import { useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

type Recipient = {
  id: string;
  patient_id: string;
  patient_name: string;
  vaccine_text: string;
  occurrence_datetime: string;
  given_by_name: string;
};

function when(iso: string) {
  return new Date(iso).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Who received a batch.
 *
 * The screen a clinic opens on a bad morning. A manufacturer recalls a lot and
 * the question is who had it, which is the reason the batch number is a
 * required field rather than a nicety (ADR-022).
 *
 * It is deliberately one box and a list. Nobody reads a dashboard during a
 * recall.
 *
 * Only a doctor or a nurse may ask. The answer names patients and says what
 * they received, which is clinical data whatever prompted the question
 * (ADR-008 point 6).
 */
export function BatchRecall() {
  const [batch, setBatch] = useState("");
  const [rows, setRows] = useState<Recipient[] | null>(null);
  const [searched, setSearched] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!batch.trim()) return;

    setBusy(true);
    const { data, error: failed } = await api.GET("/portal/immunisations/batch", {
      params: { query: { batch: batch.trim() }, header: clinicHeader() },
    });
    setBusy(false);

    if (failed) {
      setError(readError(failed).message);
      setRows(null);
      return;
    }
    setError(null);
    setSearched(data?.batch_number ?? batch.trim());
    setRows((data?.items ?? []) as Recipient[]);
  };

  return (
    <section>
      <h1>Find a batch</h1>

      {/* Opened rarely, and under pressure, by somebody who has probably never
          opened it before. So it says where the number comes from and what
          comes back, rather than assuming either. */}
      <p className="lead">
        A manufacturer recalls a lot and the question is who had it. Type the
        batch number from the recall notice; this lists everyone at this clinic
        who received it.
      </p>

      <form className="batch-find" onSubmit={search}>
        <label htmlFor="batch">Batch number</label>
        <div className="batch-row">
          <input
            id="batch"
            type="search"
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
            placeholder="for example FLU-2026-A7"
            autoFocus
          />
          <button type="submit" disabled={busy || !batch.trim()}>
            {busy ? "Looking…" : "Find"}
          </button>
        </div>
        <p className="batch-hint">
          Printed on the notice and on the vial. Upper or lower case, either
          works.
        </p>
      </form>

      <ErrorNote message={error} />

      {rows !== null && rows.length === 0 && (
        <p className="empty">
          Nobody at this clinic received <span className="mono">{searched}</span>.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="batch-answer">
            <p>
              <strong>{rows.length}</strong>{" "}
              {rows.length === 1 ? "person" : "people"} received{" "}
              <span className="mono">{searched}</span>.
            </p>
            {/* What happens next, because the list is not the end of the job
                and this screen is somebody's first ten seconds of a bad
                morning. Nothing here contacts anybody: the portal has no
                outbound channel and must not imply one. */}
            <p className="batch-next">
              Telephone them. Open a name to reach the chart, where the batch is
              recorded against the vaccination.
            </p>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Vaccine</th>
                  <th>Given</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/patients/${row.patient_id}`}>{row.patient_name}</Link>
                    </td>
                    <td>{row.vaccine_text}</td>
                    <td>{when(row.occurrence_datetime)}</td>
                    <td className="muted">{row.given_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
